import React from "react"
import { Spinner } from "../shared/Spinner"
import { RequestError } from "../shared/RequestError"
import { useComments } from "../../hooks/useComments"
import CommentBlock from "../CommentBlock"
import { CommentTreeNode } from "../../requests/comments"
import type { DisplayMode } from "../../types/postComponentDisplay"

interface CommentsData {
  premiere_blurbs: CommentTreeNode[]
  total_all_count: number
  has_next_page: boolean
  comments: CommentTreeNode[]
}

interface CommentSectionPreviewProps {
  postId: number
  postPath: string
  className?: string
  mode?: DisplayMode
  perPage?: number
}

const CommentSectionPreview: React.FC<CommentSectionPreviewProps> = ({
  postId,
  postPath,
  className = "my-4",
  mode = "preview",
  perPage = 1,
}) => {
  const postPathWithAnchor = `${postPath}#comments`
  const { isFetching, data, error } = useComments(postId, 1, perPage) as {
    isFetching: boolean
    data: CommentsData | undefined
    error: unknown
  }

  if (mode === "hide") return null

  if (mode === "hint") {
    const total = (data as any)?.total_all_count ?? 0
    return (
      <a href={postPathWithAnchor} className="text-xs text-gray-500 mt-2 block">
        {total > 0 ? `See Comments (${total})` : "Add Comment"}
      </a>
    )
  }

  const truncate = (text: string) =>
    text.length > 250 ? `${text.slice(0, 250)}…` : text

  const premiereBlurbs = data?.premiere_blurbs ?? []
  const totalAllCount = data?.total_all_count ?? 0
  const comments = data?.comments ?? []

  const totalDisplay = premiereBlurbs.length + totalAllCount

  const buildPreviewComment = (comment: CommentTreeNode) => {
    if (comment.media_type !== "direct_upload") {
      return { ...comment, comment_text: truncate(comment.comment_text) }
    }
    const text = (comment.stripped_comment_text || comment.comment_text)?.trim()
    const count = comment.media_urls?.length ?? 1
    const previewText = text
      ? truncate(text)
      : `<span class="italic text-green-600 text-xs">added ${count} ${count === 1 ? "image" : "images"}</span>`
    return {
      ...comment,
      comment_text: previewText,
      stripped_comment_text: previewText,
      media_type: "no_media",
      media_urls: [],
    }
  }

  const previewComments = [
    ...premiereBlurbs.map(buildPreviewComment),
    ...comments.map(buildPreviewComment),
  ]

  return (
    <div className={className}>
      {isFetching && !data ? (
        <Spinner />
      ) : error ? (
        <RequestError error={error} />
      ) : data ? (
        <>
          <h4 className="uppercase font-bold text-gray-700 text-xs mb-2">
            Comments{" "}
            {totalDisplay > 0 ? (
              <>
                <span className="font-normal">({totalDisplay})</span>
                <a
                  href={postPathWithAnchor}
                  className="pl-2 text-blue-500 normal-case font-normal"
                >
                  see all
                </a>
              </>
            ) : null}
          </h4>
          {previewComments.length > 0 && (
            <div className="relative cursor-pointer">
              <a
                href={postPathWithAnchor}
                className="absolute inset-0 z-10"
                aria-label="See all comments"
              />
              <ul className="pointer-events-none">
                {previewComments.map((comment, index) =>
                  comment.comment_type === "premiere_blurb" ? (
                    <CommentBlock
                      key={`preview-${comment.id}`}
                      comment={comment}
                      depth={0}
                      isTreeNode
                      postId={postId}
                      interactiveVoteBlock={index === 0}
                    />
                  ) : (
                    <CommentBlock
                      key={`preview-${comment.id}`}
                      comment={comment}
                      depth={0}
                      postPath={postPathWithAnchor}
                      interactiveVoteBlock={index === 0}
                    />
                  ),
                )}
              </ul>
            </div>
          )}
          <a href={postPathWithAnchor} className="block">
            <div className="border border-gray-200 rounded-sm bg-white px-2 py-2 text-sm text-gray-400 cursor-text">
              {totalDisplay > 0
                ? "Join the conversation..."
                : "Kick off the conversation..."}
            </div>
          </a>
        </>
      ) : null}
    </div>
  )
}

export default CommentSectionPreview
